import {
  takeLatest,
  all,
  call,
  put,
  select,
  delay,
  takeEvery,
  putResolve,
  take,
} from "redux-saga/effects";

import history from "utils/history";
import PackageApi from "ee/api/PackageApi";
import {
  ReduxActionTypes,
  ReduxActionErrorTypes,
} from "ee/constants/ReduxActionConstants";
import { validateResponse } from "sagas/ErrorSagas";
import {
  CONSUMED_PACKAGE_CHANGE_VERSION_ERROR,
  CREATE_PACKAGE_ERROR,
  createMessage,
  ERROR_IMPORT_PACKAGE,
  FETCH_PACKAGE_ERROR,
  FETCH_PACKAGES_ERROR,
  // PACKAGE_PUBLISH_SUCCESS,
  PACKAGE_PUBLISH_SUCCESS_SIMPLE,
  PACKAGE_PULL_ERROR,
} from "ee/constants/messages";
import {
  getCurrentBasePackageId,
  getIsFetchingPackages,
  getIsPackageEditorInitialized,
  getPackages,
  getPackagesList,
} from "ee/selectors/packageSelectors";
import { getNextEntityName } from "utils/AppsmithUtils";
import {
  DEFAULT_CODE_PACKAGE_PREFIX,
  DEFAULT_PACKAGE_COLOR,
  DEFAULT_PACKAGE_ICON,
  DEFAULT_UI_PACKAGE_PREFIX,
} from "ee/constants/PackageConstants";
import {
  BASE_PACKAGE_EDITOR_PATH,
  BASE_UI_PACKAGE_EDITOR_PATH,
} from "ee/constants/routes/packageRoutes";
import type { ApiResponse } from "api/ApiResponses";
import {
  fetchConsumablePackagesInWorkspace,
  fetchPackage,
  pollPackagePullStatus,
  pullPackage,
  type CreatePackageFromWorkspacePayload,
  type DeletePackagePayload,
  type FetchConsumablePackagesInWorkspacePayload,
  type FetchPackagePayload,
  type ImportPackagePayload,
  type PollPackagePullStatusPayload,
  type PublishPackagePayload,
  type PullPackagePayload,
  type RequestPullPackagePayload,
} from "ee/actions/packageActions";
import type {
  ChangeConsumedPackageVersionPayload,
  ChangeConsumedPackageVersionResponse,
  CreatePackagePayload,
  FetchConsumablePackagesInWorkspaceResponse,
  FetchPackageResponse,
  ImportPackageResponse,
  PollPackagePullStatusResponse,
  PublishPackageResponse,
  PullPackageResponse,
} from "ee/api/PackageApi";
import type { ReduxAction, ReduxActionType } from "actions/ReduxActionTypes";
import type { Package, PackageMetadata } from "ee/constants/PackageConstants";
import { PACKAGE_TYPE } from "ee/constants/PackageConstants";
import { toast } from "@appsmith/ads";
import {
  getPackagePullFeature,
  getShowQueryModule,
  getShowUIModule,
} from "ee/selectors/moduleFeatureSelectors";
import analytics from "ee/utils/Packages/analytics";
import { setUnconfiguredDatasourcesDuringImport } from "actions/datasourceActions";
import { fetchPlugins } from "actions/pluginActions";
import {
  setIsReconnectingDatasourcesModalOpen,
  setWorkspaceIdForImport,
} from "ee/actions/applicationActions";
import { fetchApplication } from "ce/actions/applicationActions";
import { APP_MODE } from "entities/App";
import { evalWorker as EvalWorker } from "utils/workerInstances";
import { EVAL_WORKER_ACTIONS } from "ee/workers/Evaluation/evalWorkerActions";
import { createDefaultUIModule } from "./moduleSagas";
import {
  getHasCyclicModuleReference,
  getActiveModuleActions,
  getAllModules,
} from "ee/selectors/modulesSelector";
import { PACKAGE_PULL_STATUS } from "ce/constants/ModuleConstants";
import {
  getCurrentApplicationId,
  getCurrentPageId,
} from "selectors/editorSelectors";
import { ModuleInstanceCreatorType } from "ee/constants/ModuleInstanceConstants";
import { setupModuleInstanceSaga } from "./moduleInstanceSagas";
import { getCurrentWorkspaceId } from "ee/selectors/selectedWorkspaceSelectors";
import type { Module } from "ee/constants/ModuleConstants";
import moduleWidgetFactory from "ee/widgets/ModuleWidgetFactory";
import { registerWidgets } from "WidgetProvider/factory/registrationHelper";
import { fetchAndRaceWithPackagePull } from "./moduleUtilsSagas";
import { MAIN_MODULE_CONTAINER_WIDGET_ID } from "ee/utils/Packages/createBaseModuleDSL";
import type { WidgetProps } from "@shared/dsl/src/migrate/types";
import type { NestedDSL } from "@shared/dsl";
import fetchRefsRequest from "git/requests/fetchRefsRequest";
import type { FetchRefsResponse } from "git/requests/fetchRefsRequest.types";
import { GitArtifactType } from "git";
import { waitForUpdatingModuleReferences } from "./moduleInterfaceSaga";
import type { RecentEntity } from "components/editorComponents/GlobalSearch/utils";
import {
  restoreRecentEntitiesSuccess,
  setRecentEntities,
} from "actions/globalSearchActions";
import { fetchRecentAppEntities, setRecentAppEntities } from "utils/storage";
import log from "loglevel";
import type { DefaultRootState } from "react-redux";
import type { FocusEntity, FocusEntityInfo } from "navigation/FocusEntity";
import { selectGitCurrentBranch } from "git/store/selectors";
import { migrateUIModuleInstanceLayout_mutate } from "ee/utils/Packages/moduleHelpers";

interface ModuleLayout {
  dsl: NestedDSL<unknown>;
  layoutOnLoadActions: string[];
}

interface CreatePackageSagaProps {
  workspaceId: string;
  name?: string;
  icon?: string;
  color?: string;
  type?: PACKAGE_TYPE;
}

interface ShowReconnectModalProps {
  package: ImportPackageResponse["package"];
  unConfiguredDatasourceList: ImportPackageResponse["unConfiguredDatasourceList"];
}

export interface FetchPackagesForWorkspacePayload {
  workspaceId: string;
}

const PUBLISH_DEPENDANT_ACTIONS = [
  ReduxActionTypes.UPDATE_MODULE_INPUTS_INIT,
  ReduxActionTypes.UPDATE_MODULE_OUTPUTS_INIT,
  ReduxActionTypes.UPDATE_MODULE_ON_INPUT_CHANGE_INIT,
  ReduxActionTypes.UPDATE_JS_FUNCTION_PROPERTY_INIT,
  ReduxActionTypes.UPDATE_MODULE_INIT,
  ReduxActionTypes.UPDATE_ACTION_INIT,
  ReduxActionTypes.SAVE_MODULE_NAME_INIT,
];

const DEFAULT_RETRY_LIMIT = 70;
const MAX_POLL_COUNT = 50;
const POLL_INTERVAL = 1000; // in ms

const getRecentEntitiesKey = (packageId: string, branch?: string) =>
  branch ? `${packageId}-${branch}` : packageId;

export function* waitUntilRunningActionsComplete(
  dependentActions: ReduxActionType[],
  retryLimit = DEFAULT_RETRY_LIMIT,
) {
  let activeActions: string[] = yield select(getActiveModuleActions);
  let currCount = 0;

  // Keep checking until all specified actions are completed
  while (dependentActions.some((action) => activeActions.includes(action))) {
    // If an operation is taking very long time or due to any bug, the completion
    // status is not dispatched; the retry limit will make sure to return false
    // for the caller saga to take appropriate action.
    if (currCount > retryLimit) {
      return false;
    }

    currCount++;

    yield delay(100); // Wait for a short delay before checking again
    activeActions = yield select(getActiveModuleActions); // Recheck the actions
  }

  return true;
}

export function* fetchPackagesForWorkspaceSaga(
  action: ReduxAction<FetchPackagesForWorkspacePayload>,
) {
  try {
    const showQueryModule: boolean = yield select(getShowQueryModule);

    if (showQueryModule) {
      const response: ApiResponse = yield call(
        PackageApi.fetchPackagesByWorkspace,
        action.payload,
      );
      const isValidResponse: boolean = yield validateResponse(response);

      if (isValidResponse) {
        yield put({
          type: ReduxActionTypes.FETCH_PACKAGES_FOR_WORKSPACE_SUCCESS,
          payload: response.data,
        });
      }
    } else {
      yield put({
        type: ReduxActionTypes.FETCH_PACKAGES_FOR_WORKSPACE_SUCCESS,
        payload: [],
      });
    }
  } catch (error) {
    yield put({
      type: ReduxActionErrorTypes.FETCH_PACKAGES_FOR_WORKSPACE_ERROR,
      payload: { error: { message: createMessage(FETCH_PACKAGES_ERROR) } },
    });
  }
}

export function* registerUIModules(
  modules: Module<ModuleLayout>[],
  packages: Package[],
) {
  const moduleWidgets = modules
    .map((module) => {
      const dsl = module?.layouts?.[0]?.dsl;
      const pkg = packages.find((p) => p.id === module.packageId);

      if (!dsl || !pkg) return null;

      const container = (dsl?.children || []).find(
        (child: WidgetProps) =>
          child.widgetId === MAIN_MODULE_CONTAINER_WIDGET_ID,
      );

      if (!container) return null;

      const moduleMetadata = {
        // Remove once the type is updated
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        rows: container?.bottomRow - container?.topRow,
        // Remove once the type is updated
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        columns: container?.rightColumn - container?.leftColumn,
      };

      return moduleWidgetFactory({
        ...moduleMetadata,
        moduleUUID: module.moduleUUID,
        packageName: pkg.name,
        widgetName: module.name,
      });
    })
    .filter(Boolean);

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  registerWidgets(moduleWidgets);
}
export function* fetchConsumablePackagesInWorkspaceSaga(
  action: ReduxAction<FetchConsumablePackagesInWorkspacePayload>,
) {
  try {
    const response: ApiResponse<FetchConsumablePackagesInWorkspaceResponse> =
      yield fetchAndRaceWithPackagePull(
        PackageApi.fetchConsumablePackagesInWorkspace,
        {
          payload: action.payload,
          path: "consumables",
        },
      );
    const isValidResponse: boolean = yield validateResponse(response);

    if (isValidResponse) {
      yield put({
        type: ReduxActionTypes.FETCH_CONSUMABLE_PACKAGES_IN_WORKSPACE_SUCCESS,
        payload: response.data,
      });
    }
  } catch (error) {
    yield put({
      type: ReduxActionErrorTypes.FETCH_CONSUMABLE_PACKAGES_IN_WORKSPACE_ERROR,
      payload: { error: { message: createMessage(FETCH_PACKAGES_ERROR) } },
    });
  }
}

/**
 * Saga creates a package and specifically should be called from workspace
 */
export function* createPackageFromWorkspaceSaga(
  action: ReduxAction<CreatePackageFromWorkspacePayload>,
) {
  try {
    const { workspaceId } = action.payload;

    const isFetchingPackagesList: boolean = yield select(getIsFetchingPackages);

    if (isFetchingPackagesList) return;

    const response: ApiResponse<Package> = yield call(createPackageSaga, {
      workspaceId,
      type: action.payload.type,
    });
    const isValidResponse: boolean = yield validateResponse(response);

    if (isValidResponse) {
      const { id } = response.data;

      yield put({
        type: ReduxActionTypes.CREATE_PACKAGE_FROM_WORKSPACE_SUCCESS,
        payload: {
          ...response.data,
          workspaceId,
        },
      });

      analytics.createPackage(response.data);

      if (response.data.type === PACKAGE_TYPE.UI) {
        history.push(`${BASE_UI_PACKAGE_EDITOR_PATH}/${id}`);
      } else {
        history.push(`${BASE_PACKAGE_EDITOR_PATH}/${id}`);
      }
    }
  } catch (error) {
    yield put({
      type: ReduxActionErrorTypes.CREATE_PACKAGE_FROM_WORKSPACE_ERROR,
      payload: {
        error: {
          message: createMessage(CREATE_PACKAGE_ERROR),
        },
      },
    });
  }
}

/**
 * Creates a package based on the workspaceId provided. name, icon and color are optional, so if
 * they are not provided; the saga will auto generate them.
 * For name, the saga will will look into existing packages in the workspace and generate the next
 * possible name.
 *
 * @param payload - CreatePackageSagaProps
 *  {
      workspaceId: string;
      name?: string;
      icon?: string;
      color?: string;
      type?: PACKAGE_TYPE;
    }
 * @returns
 */
export function* createPackageSaga(payload: CreatePackageSagaProps) {
  try {
    const packageList: PackageMetadata[] = yield select(getPackagesList);
    const showUIModule: boolean = yield select(getShowUIModule);
    const { type = PACKAGE_TYPE.CODE } = payload;

    // If requested for creating UI Package, but UI module is not enabled, throw an error
    if (type === PACKAGE_TYPE.UI && !showUIModule) {
      throw new Error("UI module is not enabled");
    }

    const name = (() => {
      if (payload.name) return payload.name;

      const currentWorkspacePackages = packageList
        .filter(({ workspaceId }) => workspaceId === payload.workspaceId)
        .map(({ name }) => name);

      return getNextEntityName(
        type === PACKAGE_TYPE.UI
          ? DEFAULT_UI_PACKAGE_PREFIX
          : DEFAULT_CODE_PACKAGE_PREFIX,
        currentWorkspacePackages,
      );
    })();

    const packageData: Omit<CreatePackagePayload, "workspaceId"> = {
      name,
      icon: payload.icon || DEFAULT_PACKAGE_ICON,
      color: payload.color || DEFAULT_PACKAGE_COLOR,
      type,
    };

    let response: ApiResponse<Package>;

    if (type === PACKAGE_TYPE.UI) {
      const defaultModule: Module = yield call(createDefaultUIModule);
      const body = {
        workspaceId: payload.workspaceId,
        packageData,
        defaultModule,
      };

      response = yield call(PackageApi.createPackageWithModule, body);
    } else {
      const body = {
        workspaceId: payload.workspaceId,

        ...packageData,
      };

      response = yield call(PackageApi.createPackage, body);
    }

    return response;
  } catch (error) {
    throw error;
  }
}

export function* fetchPackageSaga(action: ReduxAction<FetchPackagePayload>) {
  try {
    const response: ApiResponse<FetchPackageResponse> = yield call(
      PackageApi.fetchPackage,
      action.payload,
    );
    const isValidResponse: boolean = yield validateResponse(response);

    if (isValidResponse) {
      yield put({
        type: ReduxActionTypes.FETCH_PACKAGE_SUCCESS,
        payload: response.data,
      });

      return response.data;
    }
  } catch (error) {
    yield put({
      type: ReduxActionErrorTypes.FETCH_PACKAGE_ERROR,
      payload: {
        error: {
          message: createMessage(FETCH_PACKAGE_ERROR),
        },
      },
    });
  }
}

export function* updatePackageSaga(action: ReduxAction<Package>) {
  try {
    const packageData: Package = yield call(PackageApi.fetchPackage, {
      basePackageId: action.payload.baseId,
    });
    const response: ApiResponse<Package> = yield call(
      PackageApi.updatePackage,
      { ...packageData, ...action.payload },
    );
    const isValidResponse: boolean = yield validateResponse(response);

    if (isValidResponse) {
      yield put({
        type: ReduxActionTypes.UPDATE_PACKAGE_SUCCESS,
        payload: response.data,
      });

      analytics.updatePackage(response.data);

      return response.data;
    }
  } catch (error) {
    yield put({
      type: ReduxActionErrorTypes.UPDATE_PACKAGE_ERROR,
      payload: {
        error,
      },
    });
  }
}

export function* deletePackageSaga(action: ReduxAction<DeletePackagePayload>) {
  try {
    const response: ApiResponse<Package> = yield call(
      PackageApi.deletePackage,
      action.payload,
    );
    const isValidResponse: boolean = yield validateResponse(response);

    if (isValidResponse) {
      yield put({
        type: ReduxActionTypes.DELETE_PACKAGE_SUCCESS,
        payload: action.payload,
      });

      analytics.deletePackage(action.payload.id);

      return response.data;
    }
  } catch (error) {
    yield put({
      type: ReduxActionErrorTypes.DELETE_PACKAGE_ERROR,
      payload: {
        error,
      },
    });
  }
}

export function* publishPackageSaga(
  action: ReduxAction<PublishPackagePayload>,
) {
  try {
    const isUpdatingModuleReferencesSuccess: boolean = yield call(
      waitForUpdatingModuleReferences,
    );

    if (!isUpdatingModuleReferencesSuccess) {
      return;
    }

    // Wait for the dependent actions to complete
    const success: boolean = yield call(
      waitUntilRunningActionsComplete,
      PUBLISH_DEPENDANT_ACTIONS,
    );

    if (!success) {
      throw new Error(
        "Publish failed due to pending module updates. Retry after some time or refresh",
      );
    }

    const hasCyclicModuleReference: boolean = yield select(
      getHasCyclicModuleReference,
    );

    if (hasCyclicModuleReference) {
      throw new Error("Cannot publish package with cyclic dependencies.");
    }

    const response: ApiResponse<PublishPackageResponse> = yield call(
      PackageApi.publishPackage,
      action.payload,
    );
    const isValidResponse: boolean = yield validateResponse(response);

    if (isValidResponse) {
      yield put({
        type: ReduxActionTypes.PUBLISH_PACKAGE_SUCCESS,
        payload: {
          ...response.data,
          packageId: action.payload.packageId,
        },
      });

      analytics.publishPackage(action.payload.packageId);
      toast.show(createMessage(PACKAGE_PUBLISH_SUCCESS_SIMPLE), {
        kind: "success",
      });
      // toast.show(
      //   createMessage(
      //     PACKAGE_PUBLISH_SUCCESS,
      //     response.data.lastPublishedVersion,
      //   ),
      //   { kind: "success" },
      // );

      return response.data;
    }
  } catch (error) {
    yield put({
      type: ReduxActionErrorTypes.PUBLISH_PACKAGE_ERROR,
      payload: {
        error,
      },
    });
  }
}

export function* importPackageSaga(action: ReduxAction<ImportPackagePayload>) {
  try {
    const response: ApiResponse<ImportPackageResponse> = yield call(
      PackageApi.importPackage,
      action.payload,
    );

    const isValidResponse: boolean = yield validateResponse(response);

    if (isValidResponse) {
      const {
        isPartialImport,
        package: pkg,
        unConfiguredDatasourceList,
      } = response.data;

      yield put({
        type: ReduxActionTypes.IMPORT_PACKAGE_SUCCESS,
        payload: pkg,
      });

      if (isPartialImport) {
        yield call(showReconnectDatasourceModalSaga, {
          package: pkg,
          unConfiguredDatasourceList,
        });
      } else {
        if (!action.payload.packageId) {
          const packageUrl =
            pkg.type === PACKAGE_TYPE.UI
              ? `${BASE_UI_PACKAGE_EDITOR_PATH}/${pkg.baseId}`
              : `${BASE_PACKAGE_EDITOR_PATH}/${pkg.baseId}`;

          history.push(packageUrl);
        } else {
          yield call(
            fetchPackageSaga,
            fetchPackage({ basePackageId: pkg.baseId }),
          );
        }

        toast.show("Package imported successfully", {
          kind: "success",
        });
      }
    }
  } catch (e) {
    yield put({
      type: ReduxActionErrorTypes.IMPORT_PACKAGE_ERROR,
      payload: {
        error: createMessage(ERROR_IMPORT_PACKAGE),
      },
    });
  }
}

export function* showReconnectDatasourceModalSaga(
  props: ShowReconnectModalProps,
) {
  const { package: pkg, unConfiguredDatasourceList } = props;
  const { workspaceId } = pkg;

  yield put(fetchPlugins({ workspaceId }));
  yield put(
    setUnconfiguredDatasourcesDuringImport(unConfiguredDatasourceList || []),
  );
  yield put(setWorkspaceIdForImport({ editorId: pkg.id, workspaceId }));
  yield put(setIsReconnectingDatasourcesModalOpen({ isOpen: true }));
}

/**
 * For now this saga only supports initializing default js libs provided by the platform
 */
export function* fetchJSLibrariesSaga() {
  yield call(EvalWorker.request, EVAL_WORKER_ACTIONS.LOAD_LIBRARIES);
}

export function* requestPullPackageSaga(
  action: ReduxAction<RequestPullPackagePayload>,
) {
  try {
    const { packagePullStatus, pageId } = action.payload;
    const packagePullEnabled: boolean = yield select(getPackagePullFeature);

    if (packagePullEnabled) {
      const applicationId: string = yield select(getCurrentApplicationId);

      switch (packagePullStatus) {
        case PACKAGE_PULL_STATUS.UPGRADABLE:
          yield put(
            pullPackage({
              applicationId,
              pageId,
            }),
          );
          break;
        case PACKAGE_PULL_STATUS.UPGRADING:
          yield put(
            pollPackagePullStatus({
              applicationId,
              pageId,
            }),
          );
          break;
        default:
          break;
      }
    }
  } catch (e) {
    yield put({
      type: ReduxActionErrorTypes.PULL_PACKAGE_ERROR,
      payload: {
        error: createMessage(PACKAGE_PULL_ERROR),
      },
    });
  }
}

export function* pollPackagePullStatusSaga(
  action: ReduxAction<PollPackagePullStatusPayload>,
) {
  try {
    const {
      applicationId,
      maxPollCount = MAX_POLL_COUNT,
      pageId,
    } = action.payload;

    let pollCount = 0;
    let response: ApiResponse<PollPackagePullStatusResponse> | null = null;

    const workspaceId: string = yield select(getCurrentWorkspaceId);

    while (pollCount < maxPollCount) {
      response = yield call(PackageApi.pollPackagePullStatus, {
        applicationId,
      });

      const isValidResponse: boolean = yield validateResponse(response);

      if (response && isValidResponse) {
        if (response.data === PACKAGE_PULL_STATUS.UPGRADING) {
          pollCount++;
          yield delay(POLL_INTERVAL);
        } else {
          break;
        }
      }
    }

    if (response && response.data === PACKAGE_PULL_STATUS.UPGRADING) {
      yield put({
        type: ReduxActionErrorTypes.PULL_PACKAGE_ERROR,
        payload: {
          error: {
            message: createMessage(PACKAGE_PULL_ERROR),
          },
        },
      });
    } else {
      yield put(
        fetchConsumablePackagesInWorkspace({
          workspaceId,
          applicationId,
        }),
      );
      yield call(setupModuleInstanceSaga, {
        type: ReduxActionTypes.SETUP_MODULE_INSTANCE_INIT,
        payload: {
          contextId: pageId,
          contextType: ModuleInstanceCreatorType.PAGE,
          viewMode: false,
        },
      });

      yield put({
        type: ReduxActionTypes.POLL_PACKAGE_PULL_STATUS_SUCCESS,
      });
    }
  } catch (e) {
    yield put({
      type: ReduxActionErrorTypes.POLL_PACKAGE_PULL_STATUS_ERROR,
      payload: {
        error: {
          message: createMessage(PACKAGE_PULL_ERROR),
        },
      },
    });
  }
}

export function* pullPackageSaga(action: ReduxAction<PullPackagePayload>) {
  try {
    const response: ApiResponse<PullPackageResponse> = yield call(
      PackageApi.pullPackage,
      action.payload,
    );
    const isValidResponse: boolean = yield validateResponse(response);

    if (isValidResponse) {
      yield call(
        migrateUIModuleInstanceLayout_mutate,
        response.data.moduleInstances,
      );

      yield put({
        type: ReduxActionTypes.PULL_PACKAGE_SUCCESS,
        payload: response,
      });

      // Fetch application details to get updated modifiedAt and other fields
      const applicationId: string = yield select(getCurrentApplicationId);
      const pageId: string = yield select(getCurrentPageId);

      yield put(
        fetchApplication({
          applicationId,
          pageId,
          mode: APP_MODE.EDIT,
        }),
      );
    }
  } catch (e) {
    yield put({
      type: ReduxActionErrorTypes.PULL_PACKAGE_ERROR,
      payload: {
        error: {
          message: createMessage(PACKAGE_PULL_ERROR),
        },
      },
    });
  }
}

export function* changeConsumedPackageVersionSaga(
  action: ReduxAction<ChangeConsumedPackageVersionPayload>,
) {
  try {
    const currentApplicationId: string = yield select(getCurrentApplicationId);
    const currentPageId: string = yield select(getCurrentPageId);
    const payload = {
      ...action.payload,
      branchedApplicationId: currentApplicationId,
      requestPageId: currentPageId,
    };
    const response: ApiResponse<ChangeConsumedPackageVersionResponse> =
      yield call(PackageApi.changeConsumedPackageVersion, payload);
    const isValidResponse: boolean = yield validateResponse(response);

    if (isValidResponse) {
      yield call(
        migrateUIModuleInstanceLayout_mutate,
        response.data.moduleInstances,
      );

      yield put({
        type: ReduxActionTypes.CHANGE_CONSUMED_PACKAGE_VERSION_SUCCESS, // Need migration
        payload: response.data,
      });
      yield put({
        type: ReduxActionTypes.FETCH_CONSUMABLE_PACKAGES_IN_WORKSPACE_SUCCESS,
        payload: response.data.consumables,
      });
      yield put({
        type: ReduxActionTypes.SETUP_MODULE_INSTANCE_INIT,
        payload: {
          contextId: currentPageId,
          contextType: ModuleInstanceCreatorType.PAGE,
          viewMode: false,
        },
      });
    }
  } catch (e) {
    yield put({
      type: ReduxActionErrorTypes.CHANGE_CONSUMED_PACKAGE_VERSION_ERROR,
      payload: {
        error: {
          message: createMessage(CONSUMED_PACKAGE_CHANGE_VERSION_ERROR),
        },
      },
    });
  }
}

export function* fetchRemotePackageVersionsSaga(
  action: ReduxAction<{ packageId: string }>,
) {
  try {
    const { packageId } = action.payload;
    const response: FetchRefsResponse = yield call(
      fetchRefsRequest,
      GitArtifactType.Package,
      packageId,
      { refType: "tag", pruneRefs: true },
      true,
    );
    const isValidResponse: boolean = yield validateResponse(response);

    if (isValidResponse) {
      const remoteVersions = response.data.map((ref) => ref.refName);

      yield put({
        type: ReduxActionTypes.FETCH_REMOTE_PACKAGE_VERSIONS_SUCCESS,
        payload: { remoteVersions, packageId },
      });
    }
  } catch (error) {
    yield put({
      type: ReduxActionErrorTypes.FETCH_REMOTE_PACKAGE_VERSIONS_ERROR,
      payload: { error },
    });
  }
}

export function* registerUIModulesSaga() {
  const modules: ReturnType<typeof getAllModules> = yield select(getAllModules);
  const packages: ReturnType<typeof getPackages> = yield select(getPackages);
  const showUIModule: boolean = yield select(getShowUIModule);

  if (showUIModule) {
    yield call(
      registerUIModules,
      Object.values(modules) as Module<ModuleLayout>[],
      Object.values(packages),
    );
  }
}

export function* updateRecentEntitySaga(entityInfo: FocusEntityInfo) {
  try {
    const {
      entity,
      id,
      params: { baseModuleId, basePackageId = "" },
    } = entityInfo;

    const branch: string | undefined = yield select(selectGitCurrentBranch, {
      artifactType: GitArtifactType.Package,
      baseArtifactId: basePackageId,
    });

    const recentEntitiesRestored: boolean = yield select(
      (state: DefaultRootState) => state.ui.globalSearch.recentEntitiesRestored,
    );
    const isEditorInitialised: boolean = yield select(
      getIsPackageEditorInitialized,
    );

    const waitForEffects = [];

    if (!isEditorInitialised) {
      waitForEffects.push(
        take(ReduxActionTypes.INITIALIZE_PACKAGE_EDITOR_SUCCESS),
      );
    }

    if (!recentEntitiesRestored) {
      waitForEffects.push(
        take(ReduxActionTypes.RESTORE_RECENT_ENTITIES_SUCCESS),
      );
    }

    yield all(waitForEffects);

    const modules: ReturnType<typeof getAllModules> =
      yield select(getAllModules);
    const module = Object.values(modules).find(
      (module) => module.baseId === baseModuleId,
    );

    if (!module) {
      return;
    }

    let recentEntities: RecentEntity[] = yield select(
      (state: DefaultRootState) => state.ui.globalSearch.recentEntities,
    );

    recentEntities = recentEntities.slice();

    recentEntities = recentEntities.filter(
      (recentEntity: { type: FocusEntity; id: string }) =>
        recentEntity.id !== id,
    );

    recentEntities.unshift({
      type: entity,
      id,
      pageId: module.id,
    });
    recentEntities = recentEntities.slice(0, 6);

    yield put(setRecentEntities(recentEntities));

    if (basePackageId) {
      yield call(
        setRecentAppEntities,
        recentEntities,
        getRecentEntitiesKey(basePackageId, branch),
      );
    }
  } catch (e) {
    log.error(e);
  }
}

export function* restoreRecentEntities(
  actionPayload: ReduxAction<{ packageId: string; branch?: string }>,
) {
  const {
    payload: { branch, packageId },
  } = actionPayload;
  const recentEntities: RecentEntity[] = yield call(
    fetchRecentAppEntities,
    getRecentEntitiesKey(packageId, branch),
  );

  yield putResolve(setRecentEntities(recentEntities));
  yield put(restoreRecentEntitiesSuccess());
}

export function* reTriggerRecentEntities() {
  const basePackageId: string = yield select(getCurrentBasePackageId);
  const branch: string | undefined = yield select(selectGitCurrentBranch, {
    artifactType: GitArtifactType.Package,
    baseArtifactId: basePackageId,
  });
  const recentEntities: RecentEntity[] = yield call(
    fetchRecentAppEntities,
    getRecentEntitiesKey(basePackageId, branch),
  );

  yield put(setRecentEntities(recentEntities));
}

export default function* packagesSaga() {
  yield all([
    takeLatest(
      ReduxActionTypes.FETCH_PACKAGES_FOR_WORKSPACE_INIT,
      fetchPackagesForWorkspaceSaga,
    ),
    takeLatest(
      ReduxActionTypes.FETCH_CONSUMABLE_PACKAGES_IN_WORKSPACE_INIT,
      fetchConsumablePackagesInWorkspaceSaga,
    ),
    takeLatest(
      ReduxActionTypes.FETCH_REMOTE_PACKAGE_VERSIONS_INIT,
      fetchRemotePackageVersionsSaga,
    ),
    takeLatest(
      ReduxActionTypes.CREATE_PACKAGE_FROM_WORKSPACE_INIT,
      createPackageFromWorkspaceSaga,
    ),
    takeLatest(ReduxActionTypes.FETCH_PACKAGE_INIT, fetchPackageSaga),
    takeLatest(ReduxActionTypes.UPDATE_PACKAGE_INIT, updatePackageSaga),
    takeLatest(ReduxActionTypes.DELETE_PACKAGE_INIT, deletePackageSaga),
    takeLatest(ReduxActionTypes.PUBLISH_PACKAGE_INIT, publishPackageSaga),
    takeLatest(ReduxActionTypes.IMPORT_PACKAGE_INIT, importPackageSaga),
    takeLatest(
      ReduxActionTypes.FETCH_JS_LIBRARIES_FOR_PKG_INIT,
      fetchJSLibrariesSaga,
    ),
    takeLatest(
      ReduxActionTypes.REQUEST_PULL_PACKAGE_INIT,
      requestPullPackageSaga,
    ),
    takeLatest(ReduxActionTypes.PULL_PACKAGE_INIT, pullPackageSaga),
    takeLatest(
      ReduxActionTypes.POLL_PACKAGE_PULL_STATUS_INIT,
      pollPackagePullStatusSaga,
    ),
    takeLatest(
      ReduxActionTypes.CHANGE_CONSUMED_PACKAGE_VERSION_INIT,
      changeConsumedPackageVersionSaga,
    ),
    takeEvery(
      [
        ReduxActionTypes.PULL_PACKAGE_SUCCESS,
        ReduxActionTypes.FETCH_CONSUMABLE_PACKAGES_IN_WORKSPACE_SUCCESS,
      ],
      registerUIModulesSaga,
    ),
    takeLatest(
      ReduxActionTypes.RESTORE_RECENT_PACKAGE_ENTITIES_REQUEST,

      restoreRecentEntities,
    ),
    takeLatest(
      ReduxActionTypes.FETCH_MODULE_ENTITIES_SUCCESS,
      reTriggerRecentEntities,
    ),
  ]);
}
